// DS8.2 (!) comes with consts:
export const POPOVER_FOOTER_BUTTON_SIZE = 'medium';

import React from 'react';
import c from 'classnames';
import type { BiEvent } from '@wix/da-bi/pkg/events';
import { sanitizeHref } from '@wix/da-url';
import PopoverRootLayout, {
  PopoverRootLayoutProps,
} from '../../layouts/PopoverRootLayout';
import {
  ThemeOverride,
  ThemeSurface,
} from '@wix/da-react-context/pkg/ThemeContext';
import PopoverHeader, { PopoverHeaderProps } from '../../PopoverHeader';
import PopoverSimpleFooter from '../../PopoverSimpleFooter';
import SpreadLayout from '../../SpreadLayout';

export interface Props
  extends Omit<PopoverRootLayoutProps, 'header' | 'footer'>,
    Omit<PopoverHeaderProps, 'illustration'> {
  /** A slot for an illustration of some sort. Rendered before the heading text */
  headerIllustration?: React.ReactNode;

  /** A slot for the left side of the footer. Consider passing a button here */
  footerLeft?: React.ReactNode;

  /** A slot for the right side of the footer. Consider passing a button here */
  footerRight?: React.ReactNode;

  /** A slot for a message in the the footer. */
  footerMessage?: React.ReactNode;

  /** A slot for the button in the footer.  */
  footerButtonLabel?: string;
  footerButtonBiData?: BiEvent;
  footerButtonOnClick?: () => void;
  footerButtonHref?: string;
}

/** Our standard popover.
 *
 * This component is not concerned with placement or show/hiding logic. That's the responsibility of the consumer.
 */
const Popover: React.FC<Props> = ({
  heading,
  subheading,
  headerIllustration,
  footerLeft,
  footerRight,
  footerMessage,
  footerButtonLabel,
  footerButtonBiData,
  footerButtonOnClick,
  footerButtonHref,
  className,
  onBackClick,
  onCloseClick,
  ...rest
}) => {
  return (
    <ThemeOverride themeSurface={ThemeSurface.TERTIARY}>
      <PopoverRootLayout
        className={c('ds-surface-tertiary', className)}
        header={
          <PopoverHeader
            heading={heading}
            subheading={subheading}
            illustration={headerIllustration}
            onBackClick={onBackClick}
            onCloseClick={onCloseClick}
          />
        }
        footer={
          footerLeft || footerRight ? (
            <SpreadLayout left={footerLeft} right={footerRight} />
          ) : footerMessage || footerButtonLabel ? (
            <PopoverSimpleFooter
              message={footerMessage}
              label={footerButtonLabel}
              biData={footerButtonBiData}
              href={sanitizeHref(footerButtonHref)}
              onClick={footerButtonOnClick}
            />
          ) : undefined
        }
        {...rest}
      />
    </ThemeOverride>
  );
};
Popover.displayName = 'Popover';

export default Popover;
